import {
  addMonths,
  differenceInCalendarDays,
  format,
  parseISO,
  startOfDay,
  startOfMonth,
} from "date-fns"
import {
  LICENSE_COVERAGE_GENERIC_ISSUE_META,
  LICENSE_COVERAGE_ISSUE_META,
  LICENSE_COVERAGE_MONTHS_BACK,
  LICENSE_COVERAGE_MONTHS_FORWARD,
  LICENSE_COVERAGE_SEVERITY_CRITICAL,
  LICENSE_COVERAGE_SEVERITY_WARNING,
  type LicenseCoverageFilter,
} from "@/v2-backoffice-client/license/coverage/license.coverage.constants"
import type { LicenseCoverageQuery } from "@/v2-backoffice-client/license/coverage/__types__/license.coverage.query"

export type LicenseCoverageRow =
  LicenseCoverageQuery["getLicenseCoverage"][number]
export type LicenseCoverageIssueRow = LicenseCoverageRow["Issues"][number]
export type LicenseCoverageSegmentRow = LicenseCoverageRow["Segments"][number]

/** The span the timeline draws: a year back and a year ahead of today. */
export type LicenseCoverageWindow = {
  from: Date
  to: Date
  today: Date
  /** Total days in the window, the unit every bar is placed in. */
  days: number
}

export function getLicenseCoverageWindow(
  today: Date = new Date(),
): LicenseCoverageWindow {
  const day = startOfDay(today)
  const from = startOfMonth(addMonths(day, -LICENSE_COVERAGE_MONTHS_BACK))
  const to = startOfMonth(addMonths(day, LICENSE_COVERAGE_MONTHS_FORWARD + 1))
  return { from, to, today: day, days: differenceInCalendarDays(to, from) }
}

/**
 * Parses the dates the proc emits — DATE columns as "YYYY-MM-DD" through FOR
 * JSON, Datetime scalars as ISO — into a local midnight. null stays null.
 */
export function parseCoverageDate(value: string | null | undefined) {
  if (!value) return null
  const parsed = parseISO(value)
  return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed)
}

/** Position of a day in the window, 0–100, clamped to the edges. */
export function toWindowPercent(date: Date, window: LicenseCoverageWindow) {
  const offset = differenceInCalendarDays(date, window.from)
  return Math.min(100, Math.max(0, (offset / window.days) * 100))
}

export type LicenseCoverageBar = {
  leftPct: number
  widthPct: number
  /** The bar runs past the window's right edge (open ended or later). */
  openEnded: boolean
  /** The bar starts before the window's left edge. */
  clippedStart: boolean
}

/**
 * Where a span [from, to] lands in the window, or null when it lies wholly
 * outside. A null `to` is open ended: it runs to the window's edge. A bar
 * never collapses below a sliver, so a one-day invoice line stays visible.
 */
export function toCoverageBar(
  from: Date | null,
  to: Date | null,
  window: LicenseCoverageWindow,
): LicenseCoverageBar | null {
  const start = from ?? window.from
  const end = to ?? window.to
  if (end < window.from || start > window.to) return null
  const leftPct = toWindowPercent(start, window)
  const rightPct = to ? toWindowPercent(end, window) : 100
  return {
    leftPct,
    widthPct: Math.max(0.6, rightPct - leftPct),
    openEnded: !to || end > window.to,
    clippedStart: start < window.from,
  }
}

/** Month boundaries inside the window, for the axis. */
export function getCoverageMonthTicks(window: LicenseCoverageWindow) {
  const ticks: {
    pct: number
    label: string
    isYearStart: boolean
    isQuarterStart: boolean
  }[] = []
  let cursor = startOfMonth(window.from)
  while (cursor < window.to) {
    ticks.push({
      pct: toWindowPercent(cursor, window),
      label: format(cursor, cursor.getMonth() === 0 ? "MMM yy" : "MMM"),
      isYearStart: cursor.getMonth() === 0,
      isQuarterStart: cursor.getMonth() % 3 === 0,
    })
    cursor = addMonths(cursor, 1)
  }
  return ticks
}

export function getCoverageIssueMeta(code: string) {
  return LICENSE_COVERAGE_ISSUE_META[code] ?? LICENSE_COVERAGE_GENERIC_ISSUE_META
}

export type LicenseCoverageTone = "critical" | "warning" | "info" | "none"

export function toneOfSeverity(severity: number): LicenseCoverageTone {
  if (severity >= LICENSE_COVERAGE_SEVERITY_CRITICAL) return "critical"
  if (severity === LICENSE_COVERAGE_SEVERITY_WARNING) return "warning"
  if (severity > 0) return "info"
  return "none"
}

export function filterCoverageRows(
  rows: LicenseCoverageRow[],
  filter: LicenseCoverageFilter,
) {
  switch (filter) {
    case "critical":
      return rows.filter(
        (row) => row.IssueSeverity >= LICENSE_COVERAGE_SEVERITY_CRITICAL,
      )
    case "issues":
      return rows.filter((row) => row.IssueSeverity > 0)
    default:
      return rows
  }
}

/** The board's headline numbers over every row (not the filtered ones). */
export function summarizeCoverage(rows: LicenseCoverageRow[]) {
  const critical = rows.filter(
    (row) => row.IssueSeverity >= LICENSE_COVERAGE_SEVERITY_CRITICAL,
  )
  const warning = rows.filter(
    (row) => row.IssueSeverity === LICENSE_COVERAGE_SEVERITY_WARNING,
  )
  const knocking = rows.filter(
    (row) => row.IssueSeverity > 0 && row.Attempts7d > 0,
  )
  return {
    licenses: rows.length,
    critical: critical.length,
    warning: warning.length,
    knockingCustomers: new Set(knocking.map((row) => row.CustomerId)).size,
    attempts7d: knocking.reduce((sum, row) => sum + row.Attempts7d, 0),
  }
}

/** The gap an issue spans, when it has both ends inside the window. */
export function issueGapBar(
  issue: LicenseCoverageIssueRow,
  window: LicenseCoverageWindow,
) {
  if (issue.Code !== "billing_gap") return null
  return toCoverageBar(
    parseCoverageDate(issue.FromDate),
    parseCoverageDate(issue.ToDate),
    window,
  )
}
